import { RefreshCw } from "lucide-react";
import { ErrorState } from "@/components/shared/error-state";
import { IntensityIndicator } from "@/components/shared/IntensityIndicator";
import { SignalRow } from "@/components/signals/SignalRow";
import { SuggestionBasis } from "@/components/signals/SuggestionBasis";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useProcessSolution, useSolutionSignals } from "@/hooks/use-signals";
import { useSession } from "@/lib/auth-client";

/** Coarse "just now" / "Nm" / "Nh" / "Nd" ago from an ISO instant. */
function formatSince(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/**
 * The Solution Detail drawer's "Signals" tab: this Solution's OPEN signals with
 * full inline triage (reusing SignalRow), plus the latest computed suggestion +
 * basis so the tab is never empty after a Process. The Refresh button re-runs
 * `processSolution` on demand (RM only — `processSolutionOnDemand`); ADMIN sees the
 * same view read-only. Actioning a signal routes through the existing
 * confirm/apply-pod/dismiss write paths — never a second write path (§4).
 */
export function SolutionSignalsSection({ solutionId }: { solutionId: string }) {
  const { data: session } = useSession();
  const isRm = session?.user?.role === "RM";
  const { data, isLoading, isError, refetch } = useSolutionSignals(solutionId);
  const process = useProcessSolution();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Signals
        </div>
        {isRm && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={process.isPending}
            onClick={() => process.mutate(solutionId)}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${process.isPending ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        )}
      </div>

      {isLoading && (
        <div className="space-y-2" role="status" aria-label="Loading signals">
          <Skeleton className="h-16 w-full rounded-lg" />
          <Skeleton className="h-10 w-1/2 rounded-lg" />
        </div>
      )}

      {!isLoading && isError && (
        <ErrorState message="Failed to load this Solution's signals." onRetry={() => refetch()} />
      )}

      {!isLoading && !isError && data && (
        <>
          {data.signals.length > 0 ? (
            <ul className="space-y-2">
              {data.signals.map((signal) => (
                <SignalRow key={signal.id} signal={signal} hideSolutionHeader />
              ))}
            </ul>
          ) : (
            <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
              No open signals — this Solution is reconciled against its latest suggestion.
            </p>
          )}

          {data.suggestion ? (
            <div className="rounded-md border border-border bg-card p-3">
              <div className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Latest suggestion
              </div>
              <div className="mb-2 flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Suggested Intensity</span>
                <IntensityIndicator level={data.suggestion.suggestedIntensity} size="sm" />
                <span className="ml-auto text-xs text-muted-foreground">
                  processed {formatSince(data.suggestion.computedAt)}
                </span>
              </div>
              <SuggestionBasis basis={data.suggestion.basis} />
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              Not yet processed{isRm ? " — Refresh to compute a suggestion." : "."}
            </p>
          )}
        </>
      )}
    </div>
  );
}
